import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SearchCheck,
  ClipboardCheck,
  FileSearch,
  ShieldCheck,
  FileText,
  ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";

const QUALITY_IMG =
  "https://images.unsplash.com/photo-1700727448575-6f1680cd7d75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxxdWFsaXR5JTIwaW5zcGVjdGlvbiUyMGNsaXBib2FyZHxlbnwwfHx8fDE3OTI5NTc3NzB8MA&ixlib=rb-4.1.0&q=85";
const INFRA_IMG =
  "https://images.unsplash.com/photo-1582193607281-dafb7941640f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx0ZWxlY29tJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzkyOTU3NzcwfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    icon: SearchCheck,
    title: "Inspection technique",
    text: "Vérification sur site de la qualité d'exécution des travaux fibre : cheminements, soudures, raccordements, signalétique et finitions.",
  },
  {
    icon: ClipboardCheck,
    title: "Contrôle de conformité",
    text: "Contrôle des travaux au regard des référentiels et des exigences du donneur d'ordre, avec validation point par point.",
  },
  {
    icon: FileSearch,
    title: "Audits qualité",
    text: "Audits ciblés ou campagnes de contrôle sur vos réseaux : échantillonnage, constats documentés et synthèses exploitables.",
  },
  {
    icon: ShieldCheck,
    title: "Suivi QHSE",
    text: "Accompagnement qualité, hygiène, sécurité et environnement sur les chantiers : visites, constats et plans d'actions.",
  },
  {
    icon: FileText,
    title: "Remontées VQSE",
    text: "Chaque anomalie constatée dans un raccordement fait l'objet d'une remontée claire, documentée et exploitable, photos à l'appui.",
  },
];

const STEPS = [
  { num: "01", title: "Cadrage", text: "Définition du référentiel de contrôle, du périmètre et de l'échantillonnage avec le donneur d'ordre." },
  { num: "02", title: "Contrôle sur site", text: "Inspections et audits réalisés par des contrôleurs qui connaissent le terrain et ses contraintes." },
  { num: "03", title: "Rapport documenté", text: "Constats précis, illustrés et horodatés : conformités, écarts et points de vigilance." },
  { num: "04", title: "Suivi des écarts", text: "Remontées exploitables et suivi des actions correctives jusqu'à la conformité." },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Qualite = () => (
  <>
    <Seo
      title="La Qualité — Inspection, contrôle, audits & suivi QHSE | ATLAS THD"
      description="Inspection technique, contrôle de conformité, audits qualité, suivi QHSE et remontées VQSE des travaux fibre optique : l'activité qualité d'ATLAS THD."
    />

    {/* PAGE HERO */}
    <section className="border-b border-line bg-cloud" data-testid="qualite-hero">
      <div className="bg-blueprint">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-xs font-bold uppercase tracking-[0.25em] text-qualite"
            >
              Activité 02 — La Qualité
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-4 font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
            >
              Inspection et contrôle qualité des travaux fibre
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-steel"
            >
              ATLAS THD contrôle la conformité des travaux réalisés sur vos
              infrastructures : inspections techniques, audits qualité, suivi
              QHSE et remontées VQSE traçables.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-8"
            >
              <Link
                to="/contact"
                data-testid="qualite-devis-button"
                className="group inline-flex items-center gap-2 bg-midnight px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-qualite hover:text-midnight"
              >
                Parlez-nous de votre mission
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="border border-line bg-white p-3"
          >
            <img
              src={QUALITY_IMG}
              alt="Contrôleur qualité inspectant et documentant une installation"
              className="aspect-[4/3] w-full object-cover"
              data-testid="qualite-hero-image"
            />
          </motion.div>
        </div>
      </div>
    </section>

    {/* SERVICES */}
    <section className="bg-white py-20 lg:py-28" data-testid="qualite-services">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
            Nos prestations qualité
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            Des contrôles qui parlent le langage du terrain
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`group bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(10,25,47,0.25)] lg:p-10 ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}
              data-testid={`qualite-service-${i}`}
            >
              <div className="flex items-center justify-between">
                <s.icon className="h-8 w-8 text-qualite" />
                <span className="font-display text-sm font-bold text-line">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-midnight">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-steel">{s.text}</p>
            </motion.div>
          ))}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="bg-midnight p-8 lg:p-10"
          >
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-qualite">
              Pourquoi nous
            </p>
            <p className="mt-4 font-display text-2xl font-extrabold leading-snug tracking-tight text-white">
              Nos contrôleurs sont aussi des techniciens terrain.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Ils savent où regarder, quoi mesurer et comment documenter un
              écart pour qu'il soit corrigé efficacement.
            </p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* MÉTHODE */}
    <section className="border-y border-line bg-cloud py-20 lg:py-28" data-testid="qualite-method">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
              Notre méthode
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
              Un contrôle structuré, une traçabilité complète
            </h2>
          </motion.div>
          <div className="mt-10 border-l-2 border-qualite">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative border-b border-line py-6 pl-8 first:pt-0"
                data-testid={`qualite-step-${i}`}
              >
                <span className="absolute -left-[9px] top-6 h-4 w-4 border-2 border-qualite bg-white first:top-0" />
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-qualite">
                  Étape {step.num}
                </p>
                <h3 className="mt-1 font-display text-lg font-bold text-midnight">{step.title}</h3>
                <p className="mt-1 text-sm text-steel">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="border border-line bg-white p-3">
          <img
            src={INFRA_IMG}
            alt="Infrastructure télécoms inspectée dans le cadre d'un audit qualité"
            className="aspect-[4/3] w-full object-cover"
            data-testid="qualite-method-image"
          />
        </motion.div>
      </div>
    </section>

    <CtaBand title="Besoin d'un contrôle qualité sur vos travaux ?" />
  </>
);

export default Qualite;
